/**
 * SelfImprovementTab — the generative-network self-improvement surface.
 * GENERATIVE-NETWORKS.md §6a/§6b.
 *
 * Three sections, wiring ``manifestHealthApi``:
 *   1. Health report   — per-node stats matrix (selection / judge-override / escalation
 *                        + advisory flags) + the network satisfaction (thumbs) rate.
 *   2. Rate last run   — thumbs up/down → the OUTCOME-QUALITY ANCHOR that keeps the
 *                        optimizer honest.
 *   3. Improve         — the slow-loop suggestions; the user SELECTS the ones they
 *                        agree with → we frame a revise prompt → hand it to the Build
 *                        tab (generative) → architect2 re-designs → approve → effect.
 *
 * Nothing here edits the network. It surfaces the signal and hands a prompt to the
 * existing revise route — user collaboration, not a silent update.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, ThumbsUp, ThumbsDown, Wand2, Check } from 'lucide-react';
import {
  getManifestHealth, rateNetworkRun, getManifestSuggestions, composeRevisePrompt,
} from '@stepflow/ui-shared/services/manifestHealthApi.js';

const FLAG_LABEL = {
  DEAD_NODE: 'Never selected',
  ALWAYS_ESCALATES: 'Always escalates',
  OVERCONFIDENT: 'Overconfident',
  WELL_CALIBRATED: 'Well-calibrated',
};
const FLAG_TONE = {
  DEAD_NODE: 'bg-red-50 text-red-700 ring-red-200',
  ALWAYS_ESCALATES: 'bg-amber-50 text-amber-800 ring-amber-200',
  OVERCONFIDENT: 'bg-amber-50 text-amber-800 ring-amber-200',
  WELL_CALIBRATED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
};

function pct(x) {
  return x == null ? '—' : `${Math.round(x * 100)}%`;
}

export default function SelfImprovementTab({ network, onReviseWithPrompt }) {
  const networkId = network?.networkId;
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rating, setRating] = useState(null);         // 'up' | 'down' | null
  const [suggestions, setSuggestions] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!networkId || networkId === 'new') return;
    setLoading(true); setError(null);
    try {
      const h = await getManifestHealth(networkId);
      setHealth(h);
    } catch (e) {
      setError(e?.message || 'Failed to load health');
    } finally {
      setLoading(false);
    }
  }, [networkId]);

  useEffect(() => { load(); }, [load]);

  const rate = useCallback(async (up) => {
    if (!networkId) return;
    setRating(up ? 'up' : 'down');
    try {
      await rateNetworkRun(networkId, up);
      load();  // refresh satisfaction
    } catch (e) {
      setError(e?.message || 'Rating failed');
    }
  }, [networkId, load]);

  const propose = useCallback(async () => {
    if (!networkId) return;
    setBusy(true); setError(null);
    try {
      const s = await getManifestSuggestions(networkId);
      setSuggestions(s);
      // Pre-select all proposals by default — the user unticks what they disagree with.
      setSelectedIds(new Set((s.proposals || []).map((p) => p.id)));
    } catch (e) {
      setError(e?.message || 'Could not compute suggestions');
    } finally {
      setBusy(false);
    }
  }, [networkId]);

  const toggle = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const applyToRevise = useCallback(async () => {
    if (!networkId || selectedIds.size === 0) return;
    setBusy(true); setError(null);
    try {
      const { prompt } = await composeRevisePrompt(networkId, [...selectedIds]);
      if (prompt && onReviseWithPrompt) {
        // Hand the framed prompt to the Build tab with generative on — the user
        // reviews + sends it through the normal revise route (approve → effect).
        onReviseWithPrompt(prompt);
      }
    } catch (e) {
      setError(e?.message || 'Could not frame the revise prompt');
    } finally {
      setBusy(false);
    }
  }, [networkId, selectedIds, onReviseWithPrompt]);

  const net = health?.network || {};
  const nodes = health?.nodes || [];
  const proposals = suggestions?.proposals || [];

  if (!networkId || networkId === 'new') {
    return <div className="p-6 text-sm text-gray-500">Build the network first — self-improvement learns from its runs.</div>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5" data-testid="self-improvement-tab">
      {/* Header + satisfaction + rate */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Self-improvement</h2>
          <p className="text-[11px] text-gray-500">
            {loading ? 'Loading run history…'
              : `${net.runs || 0} run${net.runs === 1 ? '' : 's'} · satisfaction ${pct(net.satisfaction)}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-500">Rate the last run:</span>
          <button type="button" onClick={() => rate(true)}
            data-testid="rate-up"
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${rating === 'up' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <ThumbsUp className="h-3.5 w-3.5" /> Good
          </button>
          <button type="button" onClick={() => rate(false)}
            data-testid="rate-down"
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${rating === 'down' ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <ThumbsDown className="h-3.5 w-3.5" /> Poor
          </button>
        </div>
      </div>

      {error ? <p className="mb-3 text-xs text-red-600">{error}</p> : null}

      {/* Health matrix */}
      <div className="mb-5 overflow-x-auto rounded-lg border border-gray-100">
        <table className="w-full text-left text-[11px]">
          <thead className="border-b border-gray-100 bg-gray-50/80 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
            <tr>
              <th className="px-2 py-1.5">Agent</th>
              <th className="px-2 py-1.5">Selected</th>
              <th className="px-2 py-1.5">Avg confidence</th>
              <th className="px-2 py-1.5">Judge override</th>
              <th className="px-2 py-1.5">Escalation</th>
              <th className="px-2 py-1.5">Signals</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {nodes.length === 0 ? (
              <tr><td colSpan={6} className="px-2 py-3 text-center text-gray-400">No run data yet.</td></tr>
            ) : nodes.map((n) => (
              <tr key={n.nodePath} className="align-top">
                <td className="px-2 py-1.5 font-medium text-gray-700" title={n.nodePath}>{n.agentId}</td>
                <td className="px-2 py-1.5 tabular-nums text-gray-600">{pct(n.selectionRate)}</td>
                <td className="px-2 py-1.5 tabular-nums text-gray-600">{pct(n.avgConfidence)}</td>
                <td className="px-2 py-1.5 tabular-nums text-gray-600">{pct(n.judgeOverrideRate)}</td>
                <td className="px-2 py-1.5 tabular-nums text-gray-600">{pct(n.escalationRate)}</td>
                <td className="px-2 py-1.5">
                  <div className="flex flex-wrap gap-1">
                    {(n.flags || []).map((f) => (
                      <span key={f} className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ring-1 ring-inset ${FLAG_TONE[f] || 'bg-slate-100 text-slate-700 ring-slate-200'}`}>
                        {FLAG_LABEL[f] || f}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Propose improvements */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-gray-800">Improvement suggestions</h3>
        <button type="button" onClick={propose} disabled={busy}
          data-testid="propose-improvements"
          className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          Propose improvements
        </button>
      </div>

      {suggestions ? (
        proposals.length === 0 ? (
          <p className="mt-2 text-[11px] text-gray-400">{suggestions.note || 'No changes suggested.'}</p>
        ) : (
          <div className="mt-2 space-y-1.5">
            {proposals.map((p) => (
              <label key={p.id} className="flex cursor-pointer items-start gap-2 rounded-md border border-gray-100 p-2 hover:bg-gray-50" data-testid={`suggestion-${p.id}`}>
                <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggle(p.id)}
                  className="mt-0.5" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[12px] text-gray-800">{p.suggestion || p.type}</span>
                  {(p.recentCritiques || []).length ? (
                    <span className="mt-0.5 block text-[10px] text-gray-400">
                      Judge notes: {(p.recentCritiques || []).slice(0, 2).join(' · ')}
                    </span>
                  ) : null}
                </span>
              </label>
            ))}
            <button type="button" onClick={applyToRevise} disabled={busy || selectedIds.size === 0}
              data-testid="apply-revise"
              className="mt-2 inline-flex items-center gap-1 rounded-md border border-blue-300 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50">
              <Check className="h-3.5 w-3.5" />
              Revise network with {selectedIds.size} selected
            </button>
          </div>
        )
      ) : null}
    </div>
  );
}
